/**
 * Debug Service for Lab Partner Chat
 * Handles debug commands like saving chat history and timing metrics
 */

import { ChatMessage } from './chat/types';

interface TimingMetrics {
  requestId: string;
  sessionId: string;
  requestStartTime: number;
  firstDeltaTime?: number;
  lastDeltaTime?: number;
  deltaCount: number;
  longestGapMs: number;
  doneTime?: number;
}

class DebugService {
  private timingEnabled = false;
  private idsEnabled = false;
  private timingData: Map<string, TimingMetrics> = new Map();
  private cleanupInterval?: NodeJS.Timeout;

  constructor() {
    // Clean up old timing data every 5 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanupOldTimingData();
    }, 5 * 60 * 1000);
  }

  isTimingEnabled(): boolean {
    return this.timingEnabled;
  }

  setTimingEnabled(enabled: boolean): void {
    this.timingEnabled = enabled;
    if (!enabled) {
      // Clear existing timing data when disabled
      this.timingData.clear();
    }
  }

  isIdsEnabled(): boolean {
    return this.idsEnabled;
  }

  setIdsEnabled(enabled: boolean): void {
    this.idsEnabled = enabled;
  }

  recordRequestStart(requestId: string, sessionId: string): void {
    if (!this.timingEnabled) return;

    this.timingData.set(requestId, {
      requestId,
      sessionId,
      requestStartTime: Date.now(),
      deltaCount: 0,
      longestGapMs: 0,
    });
  }

  recordFirstDelta(requestId: string): void {
    if (!this.timingEnabled) return;

    const metrics = this.timingData.get(requestId);
    if (!metrics) return;

    const now = Date.now();
    if (!metrics.firstDeltaTime) {
      metrics.firstDeltaTime = now;
      metrics.lastDeltaTime = now;
    }
  }

  recordDelta(requestId: string): void {
    if (!this.timingEnabled) return;

    const metrics = this.timingData.get(requestId);
    if (!metrics) return;

    const now = Date.now();
    metrics.deltaCount++;

    // Calculate gap from last delta
    if (metrics.lastDeltaTime) {
      const gap = now - metrics.lastDeltaTime;
      if (gap > metrics.longestGapMs) {
        metrics.longestGapMs = gap;
      }
    }

    metrics.lastDeltaTime = now;
  }

  recordDone(requestId: string): void {
    if (!this.timingEnabled) return;

    const metrics = this.timingData.get(requestId);
    if (!metrics) return;

    metrics.doneTime = Date.now();
  }

  getTimingMetrics(requestId: string): TimingMetrics | undefined {
    return this.timingData.get(requestId);
  }

  generateIdReport(sessionId: string, requestId: string): string | null {
    if (!this.idsEnabled) return null;

    // Format the report as a simple display of IDs
    const report = `🆔 Lab Partner Request IDs

• Session ID: ${sessionId}

• Request ID: ${requestId}`;

    return report;
  }

  generateTimingReport(requestId: string): string | null {
    if (!this.timingEnabled) return null;

    const metrics = this.timingData.get(requestId);
    if (!metrics || !metrics.doneTime) return null;

    const totalMs = metrics.doneTime - metrics.requestStartTime;
    const timeToFirstResponseMs = metrics.firstDeltaTime
      ? metrics.firstDeltaTime - metrics.requestStartTime
      : 0;
    const streamDurationMs = metrics.firstDeltaTime && metrics.doneTime
      ? metrics.doneTime - metrics.firstDeltaTime
      : 0;

    // Calculate average delta interval from first/last delta times and count
    let avgDeltaIntervalMs = 0;
    if (metrics.deltaCount > 1 && metrics.firstDeltaTime && metrics.lastDeltaTime) {
      avgDeltaIntervalMs = (metrics.lastDeltaTime - metrics.firstDeltaTime) / (metrics.deltaCount - 1);
    }

    // Convert all times to decimal seconds
    const totalSec = (totalMs / 1000).toFixed(3);
    const timeToFirstResponseSec = (timeToFirstResponseMs / 1000).toFixed(3);
    const streamDurationSec = (streamDurationMs / 1000).toFixed(3);
    const avgDeltaIntervalSec = (avgDeltaIntervalMs / 1000).toFixed(3);
    const longestGapSec = (metrics.longestGapMs / 1000).toFixed(3);

    // Format the report as a simple bullet list
    const report = `⏱️ Lab Partner Timing Metrics

Request ID: ${requestId}

• Total Time: ${totalSec}s
• Time to First Response: ${timeToFirstResponseSec}s
• Stream Duration: ${streamDurationSec}s
• Text Deltas Received: ${metrics.deltaCount}
• Avg Delta Interval: ${avgDeltaIntervalSec}s
• Longest Delta Gap: ${longestGapSec}s`;

    // Clean up this timing data
    this.timingData.delete(requestId);

    return report;
  }

  private cleanupOldTimingData(): void {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes

    for (const [requestId, metrics] of this.timingData.entries()) {
      if (now - metrics.requestStartTime > maxAge) {
        this.timingData.delete(requestId);
      }
    }
  }

  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
  }

  exportChatToMarkdown(messages: ChatMessage[], sessionId: string, modelId: string, outcomeId: string): string {
    const timestamp = new Date().toLocaleString();

    const lines: string[] = [
      '# Lab Partner Chat Export',
      '',
      `**Session ID:** ${sessionId}`,
      `**Model ID:** ${modelId}`,
      `**Outcome ID:** ${outcomeId}`,
      `**Export Date:** ${timestamp}`,
      `**Message Count:** ${messages.length}`,
      '',
      '---',
      ''
    ];

    // Add each message
    for (const message of messages) {
      const messageTime = message.timestamp.toLocaleString();

      if (message.sender === 'user') {
        lines.push(`### 👤 User (${messageTime})`);
        lines.push('');
        lines.push(message.text);
        lines.push('');
      } else {
        lines.push(`### 🤖 Lab Partner (${messageTime})`);
        lines.push('');

        // Handle regular text or labPartnerResponse
        if (message.labPartnerResponse) {
          lines.push(message.labPartnerResponse.response);

          // Add error if present
          if (message.labPartnerResponse.error) {
            lines.push('');
            lines.push('**Error:**');
            lines.push(`- Code: ${message.labPartnerResponse.error.code}`);
            lines.push(`- Message: ${message.labPartnerResponse.error.message}`);
          }

          // Add model actions if present
          if (message.labPartnerResponse.modelActions && message.labPartnerResponse.modelActions.length > 0) {
            lines.push('');
            lines.push('**Model Actions:**');
            for (const action of message.labPartnerResponse.modelActions) {
              const statusEmoji = action.status === 'succeeded' ? '✅' : action.status === 'failed' ? '❌' : '⏳';
              lines.push(`- ${statusEmoji} ${action.action}: ${action.response}`);
            }
          }

          // Add feature actions if present
          if (message.labPartnerResponse.featureActions && message.labPartnerResponse.featureActions.length > 0) {
            lines.push('');
            lines.push('**Feature Actions:**');
            for (const action of message.labPartnerResponse.featureActions) {
              const statusEmoji = action.status === 'succeeded' ? '✅' : action.status === 'failed' ? '❌' : '⏳';
              lines.push(`- ${statusEmoji} ${action.action}: ${action.feature.name}`);
              lines.push(`  - Formula: \`${action.feature.formula}\``);
            }
          }
        } else {
          lines.push(message.text);
        }

        lines.push('');
      }

      lines.push('---');
      lines.push('');
    }

    return lines.join('\n');
  }

  downloadMarkdown(content: string, sessionId: string): void {
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const filename = `lab-partner-chat-${sessionId}-${date}.md`;

    // Create blob and download
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export const debugService = new DebugService();
